import React, { MouseEventHandler, ReactNode } from "react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import styled from "@emotion/styled";
import { isMobile } from "src/utils/userSegment";
import { compassColors } from "@utils/styling";
import { zIndexModal } from "../../styles/zIndices";
import { resetFormElement } from "src/design-system/styles/resets";
import SvgCalendar from "../../icons/IconCalendar.svg";
import IconClose from "../../icons/IconClose";
import TextInput from "../TextInput/TextInput";
import { flexMiddle } from "src/design-system/styles/layout";

const { lagoon } = compassColors;

const CalendarContainerWrapper = styled.div`
  // Centers the desktop date picker pop up with the input.
  width: 400px;
  display: flex;
  justify-content: center;
`;

type CalendarContainerProps = {
  className?: string;
  children: ReactNode;
};

const CalendarContainer = ({ className, children }: CalendarContainerProps) => (
  <CalendarContainerWrapper>
    <div className={className}>{children}</div>
  </CalendarContainerWrapper>
);

const DateInputContainer = styled.div`
  ${flexMiddle};
  flex-direction: column;
  width: 100%;
  max-width: 400px;

  // When react-date-picker is rendered 'withPortal = true' (in our case, when
  // 'device_is_mobile'), the element is rendered with an classless <div>
  // surrounding it. This targets that div to style it properly.
  & > div {
    width: 100%;
  }

  & .react-datepicker-popper {
    ${zIndexModal}
  }
`;

const StyledDatePicker = styled(DatePicker)<{
  selectedDate: Date | null | undefined;
}>`
  ${(props) =>
    props.selectedDate &&
    `
      border: 1px solid ${lagoon};
      padding: 17px 19px;
    `}
`;

const ClearButton = styled.button`
  ${resetFormElement}

  display: flex;
  justify-content: center;

  :hover svg > path {
    stroke: ${lagoon};
  }
`;

type DatePickerProps = {
  /** The date format to display on the input when a date is selected. `react-datepicker` uses the
   * [date-fns formats](https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md). */
  dateFormat: ReactDatePickerProps["dateFormat"];
  placeholderText: ReactDatePickerProps["placeholderText"];
  selectedDate?: ReactDatePickerProps["selected"];
  onDateChange: ReactDatePickerProps["onChange"];
  minDate?: ReactDatePickerProps["minDate"];
};

type DateInputProps = {
  /** Called when the "x" button on the input is clicked after a date has already been selected. */
  onClearClick?: MouseEventHandler<HTMLButtonElement>;
  "data-cy"?: string;
} & DatePickerProps;

/**
 * An input which pops up a `react-datepicker` calendar when clicked, for the user to select a date.
 */
export default function DateInput({
  dateFormat,
  placeholderText,
  selectedDate,
  onDateChange,
  onClearClick,
  minDate,
  "data-cy": dataCy,
}: DateInputProps) {
  return (
    <DateInputContainer>
      <StyledDatePicker
        selectedDate={selectedDate}
        selected={selectedDate}
        onChange={onDateChange}
        dateFormat={dateFormat}
        placeholderText={placeholderText}
        minDate={minDate}
        customInput={
          <TextInput
            hasError={false}
            labelStyle="hidden"
            label={placeholderText}
            data-cy={dataCy}
            suffixElement={
              selectedDate ? (
                <ClearButton onClick={onClearClick}>
                  <IconClose />
                </ClearButton>
              ) : (
                <img src={SvgCalendar} alt="" />
              )
            }
            isSuffixInteractive={!!selectedDate}
          />
        }
        withPortal={isMobile()}
        onFocus={(e) => {
          e.target.readOnly = true;
        }}
        calendarContainer={CalendarContainer}
      />
    </DateInputContainer>
  );
}
